// src/components/operationCenter/widgets/ScatterChartWidget.tsx
// Widget wrapper for ScatterChart from TokenScreener

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import type { WidgetProps, ScatterChartConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useTokenCategoryMap } from "@/hooks/tokens/compare/useCompareData";
import { filterTokenRows } from "@/utils/tokens";
import { TimeRangePicker } from "@/components/common";
import { MetricKeys } from "@/constants/metrics";
import ScatterChart from "@/components/tokenScreener/charts/ScatterChart";
import type { MetricInfo } from "@/types/metrics";
import type { CategoryTreeNode } from "@/types/dashboards/categories";
import type { TokenRow } from "@/types/movers";

type RangeKey = '1d' | '7d' | '30d' | '365d';

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.Volume, label: "Volume" },
  { key: MetricKeys.TVL, label: "TVL" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
  { key: MetricKeys.FDV, label: "FDV" },
  { key: MetricKeys.Fees, label: "Fees" },
];

// Set of valid metric keys for quick lookup
const VALID_METRIC_KEYS = new Set(METRIC_OPTIONS.map(m => m.key));

// Validate and normalize metric key - ensures config values are valid dropdown options
const getValidMetricKey = (configValue: string | undefined, defaultValue: string): string => {
  if (configValue && VALID_METRIC_KEYS.has(configValue)) {
    return configValue;
  }
  return defaultValue;
};

const ScatterChartWidget: React.FC<WidgetProps<ScatterChartConfig>> = ({ 
  widget,
  onConfigChange 
}) => {
  const config = widget.config as ScatterChartConfig;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(300);

  // Measure container height for responsive chart
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      const height = container.clientHeight;
      if (height > 0) setContainerHeight(height);
    };
    
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(container);
    
    return () => observer.disconnect();
  }, []);

  // State - initialized from config with validation to ensure metrics are valid dropdown options
  const [xMetric, setXMetric] = useState(() => getValidMetricKey(config.x_metric, MetricKeys.PriceClose));
  const [yMetric, setYMetric] = useState(() => getValidMetricKey(config.y_metric, MetricKeys.MarketCap));
  const [xUseChange, setXUseChange] = useState(config.x_use_change ?? false);
  const [yUseChange, setYUseChange] = useState(config.y_use_change ?? false);
  const [range, setRange] = useState<RangeKey>((config.time_range as RangeKey) ?? '7d');
  const initialCategory = useMemo(
    () => config.category ?? config.categories?.[0] ?? 'all',
    [config.category, config.categories]
  );
  const [category, setCategory] = useState<string>(initialCategory);
  const [logX, setLogX] = useState(false);
  const [logY, setLogY] = useState(false);
  const [logAvailability, setLogAvailability] = useState({ x: true, y: true });

  // Data fetching
  const { data: rows } = useTokensScreenerData();
  const { roots: categoryRoots, maps } = useCategoriesTree();
  const { data: tokenCategoryMap } = useTokenCategoryMap();

  // Metrics catalog
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { 
      if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; 
    });
    return map;
  }, [catalog]);

  // Label lookup with METRIC_OPTIONS fallback
  const labelFromCatalog = useCallback((key: string): string => {
    const entry = catalogMap[key] as unknown as { display_name?: string | null; label?: string | null; name?: string | null } | undefined;
    return (entry?.display_name || entry?.label || entry?.name || METRIC_OPTIONS.find(m => m.key === key)?.label || key);
  }, [catalogMap]);

  // Check if metric supports change mode
  const supportsChange = useCallback((key: string): boolean => {
    if (!key) return false;
    if (key.startsWith('ratio:')) return false;
    const metric = catalogMap[key];
    if (!metric) return true;
    return metric.agg_fn !== 'snapshot';
  }, [catalogMap]);

  // Filter rows by category
  const rowsFiltered = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return filterTokenRows(rows as TokenRow[], {
      search: '',
      mcMin: '',
      mcMax: '',
      category,
      maps,
      tokenCategoryMap: (tokenCategoryMap as unknown as Record<string, number[]>) || {},
      excludeStables: true,
      getTicker: (r) => String(r["tokenSymbol"] ?? ''),
      getName: (r) => String(r["tokenName"] ?? r["name"] ?? ''),
      getMarketCap: (r) => (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null,
    });
  }, [rows, category, maps, tokenCategoryMap]);

  // Get flat list of categories
  const categoryOptions = useMemo(() => {
    if (!categoryRoots) return [];
    const getLeaves = (node: CategoryTreeNode): CategoryTreeNode[] => {
      if (node.is_leaf) return [node];
      return (node.children || []).flatMap(getLeaves);
    };
    return categoryRoots
      .filter((c) => c.category_code !== 'overview')
      .flatMap((root) => getLeaves(root));
  }, [categoryRoots]);

  // Handlers
  const handleXMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setXMetric(metric);
    onConfigChange?.({ x_metric: metric });
    // Reset change toggle if metric doesn't support it
    if (!supportsChange(metric) && xUseChange) {
      setXUseChange(false);
      onConfigChange?.({ x_use_change: false });
    }
  }, [onConfigChange, supportsChange, xUseChange]);

  const handleYMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setYMetric(metric);
    onConfigChange?.({ y_metric: metric });
    if (!supportsChange(metric) && yUseChange) {
      setYUseChange(false);
      onConfigChange?.({ y_use_change: false });
    }
  }, [onConfigChange, supportsChange, yUseChange]);

  const handleRangeChange = useCallback((newRange: RangeKey) => {
    setRange(newRange);
    onConfigChange?.({ time_range: newRange });
  }, [onConfigChange]);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setCategory(next);
    onConfigChange?.({ category: next });
  }, [onConfigChange]);

  const handleXChangeToggle = useCallback(() => {
    const newValue = !xUseChange;
    setXUseChange(newValue);
    onConfigChange?.({ x_use_change: newValue });
  }, [xUseChange, onConfigChange]);

  const handleYChangeToggle = useCallback(() => {
    const newValue = !yUseChange;
    setYUseChange(newValue);
    onConfigChange?.({ y_use_change: newValue });
  }, [yUseChange, onConfigChange]);

  const handleLogXToggle = useCallback(() => {
    setLogX(prev => !prev);
  }, []);

  const handleLogYToggle = useCallback(() => {
    setLogY(prev => !prev);
  }, []);

  // Sync state with config when config changes externally (with validation)
  useEffect(() => {
    if (config.x_metric) {
      // Validate that config metric is a valid dropdown option
      const validMetric = getValidMetricKey(config.x_metric, MetricKeys.PriceClose);
      setXMetric(validMetric);
    }
    if (config.y_metric) {
      // Validate that config metric is a valid dropdown option
      const validMetric = getValidMetricKey(config.y_metric, MetricKeys.MarketCap);
      setYMetric(validMetric);
    }
    if (config.time_range) {
      setRange(config.time_range as RangeKey);
    }
    if (config.x_use_change !== undefined) {
      setXUseChange(config.x_use_change);
    }
    if (config.y_use_change !== undefined) {
      setYUseChange(config.y_use_change);
    }
    if (config.category || config.categories?.[0]) {
      setCategory(config.category ?? config.categories?.[0] ?? 'all');
    }
  }, [config.x_metric, config.y_metric, config.time_range, config.x_use_change, config.y_use_change, config.category, config.categories]);

  const metricLabel = useCallback((metric: string) => {
    return METRIC_OPTIONS.find((m) => m.key === metric)?.label ?? metric;
  }, []);

  const categoryLabel = useMemo(() => {
    if (category === "all") return "All Tokens";
    const match = categoryOptions.find((cat) => cat.category_code === category);
    return match?.category_name ?? category;
  }, [category, categoryOptions]);

  const chartTitle = useMemo(() => {
    const xLabel = `${metricLabel(xMetric)}${xUseChange ? " %" : ""}`;
    const yLabel = `${metricLabel(yMetric)}${yUseChange ? " %" : ""}`;
    return `Scatter Plot - ${xLabel} vs ${yLabel}, ${categoryLabel}`;
  }, [metricLabel, xMetric, yMetric, xUseChange, yUseChange, categoryLabel]);

  const xSupportsChange = supportsChange(xMetric);
  const ySupportsChange = supportsChange(yMetric);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {chartTitle}
        </span>
      </div>
      {/* Controls */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        {/* X-axis controls */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500">X:</span>
          <select
            value={xMetric}
            onChange={handleXMetricChange}
            className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
          >
            {METRIC_OPTIONS.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
          {xSupportsChange && (
            <button
              onClick={handleXChangeToggle}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                xUseChange 
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                  : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
              }`}
            >
              %
            </button>
          )}
          {logAvailability.x && (
            <button
              onClick={handleLogXToggle}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                logX 
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                  : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
              }`}
            >
              log
            </button>
          )}
        </div>

        {/* Y-axis controls */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500">Y:</span>
          <select
            value={yMetric}
            onChange={handleYMetricChange}
            className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
          >
            {METRIC_OPTIONS.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
          {ySupportsChange && (
            <button
              onClick={handleYChangeToggle}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                yUseChange 
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                  : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
              }`}
            >
              %
            </button>
          )}
          {logAvailability.y && (
            <button
              onClick={handleLogYToggle}
              className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                logY 
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                  : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
              }`}
            >
              log
            </button>
          )}
        </div>

        {/* Category selector */}
        <select
          value={category}
          onChange={handleCategoryChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0 max-w-[100px]"
        >
          <option value="all">All</option>
          {categoryOptions.map((cat) => (
            <option key={cat.category_code} value={cat.category_code}>
              {cat.category_name}
            </option>
          ))}
        </select>

        {/* Time range */}
        <div className="ml-auto">
          <TimeRangePicker
            options={['1d', '7d', '30d', '365d'] as const}
            value={range}
            onChange={(v) => handleRangeChange(v as RangeKey)}
            theme="blue"
          />
        </div>
      </div>

      {/* Chart container */}
      <div ref={chartContainerRef} className="flex-1 min-h-0 overflow-hidden">
        <ScatterChart
          rows={rowsFiltered as Array<Record<string, unknown>>}
          range={range}
          xKey={xMetric}
          yKey={yMetric}
          xUseChange={xSupportsChange ? xUseChange : false}
          yUseChange={ySupportsChange ? yUseChange : false}
          logX={logX}
          logY={logY}
          setLogAvailability={setLogAvailability}
          search=""
          mcMin=""
          mcMax=""
          height={containerHeight}
          labelFromCatalog={labelFromCatalog}
        />
      </div>
    </div>
  );
};

export default ScatterChartWidget;
